// R1c integrations — real health board + connect/verify/disconnect/rotate.
// B2a: every card state comes from the same truth source as the top "Provider status" panel
// (GET /integration-truth → services/integration_truth.py): five allowed states + the SendGrid level.
import React, { useEffect, useMemo, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import StatusChip from "../../components/StatusChip";
import IntegrationConnectDrawer from "../../components/IntegrationConnectDrawer";
import IntegrationTruthPanel, { LEVEL_LABEL, when } from "../../components/IntegrationTruthPanel";
import IntegrationErrorHistory from "../../components/comms/IntegrationErrorHistory";
import { toast } from "sonner";
import { Plug, RotateCcw, Trash2, PlayCircle, ShieldCheck, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const TIER_LABEL = {
  1: "Tier 1 · API key · live verify",
  2: "Tier 2 · OAuth2 · scaffolded",
  3: "Tier 3 · configuration only",
};

// A provider already verified (e.g. via the platform key) or externally blocked offers no Connect action.
const NO_CONNECT_STATES = new Set(["connected_verified", "externally_blocked"]);

export default function IntegrationsAdmin() {
  const { user } = useAuth();
  const isOwner = user?.role_key === "OWNER_SUPER_ADMIN";
  const isAdmin = user?.role_key === "ADMINISTRATOR";
  const canConnect = isOwner;
  const canVerify = isOwner || isAdmin;
  const [items, setItems] = useState([]);
  const [drawer, setDrawer] = useState({ open: false, adapter: null, mode: "connect" });
  const [busyKey, setBusyKey] = useState(null);
  const [truth, setTruth] = useState(null);
  const [truthError, setTruthError] = useState(null);

  const load = async () => {
    try { setItems((await api.get("/integrations")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    try { setTruth((await api.get("/integration-truth")).data); setTruthError(null); }
    catch { setTruthError("Provider status is unavailable right now."); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const out = {};
    for (const it of items) (out[it.tier || 3] ??= []).push(it);
    return out;
  }, [items]);

  const openConnect = (it) => setDrawer({ open: true, adapter: it, mode: "connect" });
  const openRotate = (it) => setDrawer({ open: true, adapter: it, mode: "rotate" });

  // STEP 2 — Microsoft 365 primary Connect routes to a BACKEND GET that
  // issues a 302 to Microsoft. STEP 2 Physical-Device Repair: the CTA is
  // now a native <a target="_top"> link (not a JS button). iPad Safari
  // inside the Preview iframe wrapper silently blocked JS-driven
  // cross-origin top-level assignments — the sandboxed subframe never
  // escaped the frame, so Owner's view disappeared but Microsoft's
  // consent screen was never reached. The native <a> + the `_top`
  // target lets the browser perform a user-gesture top-level
  // navigation that Safari honors across iframes.
  // Secondary "Edit credentials" button (further below) still invokes
  // openConnect() for the Tier-2 credential drawer; the drawer path is
  // never reached from the primary CTA.
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const M365_CONNECT_START = `${BACKEND_URL}/api/admin/integrations/microsoft365/connect-start`;

  const verify = async (it) => {
    if (!canVerify) return;
    setBusyKey(it.key);
    try {
      const r = await api.post(`/integrations/${it.key}/verify`);
      toast[r.data.ok ? "success" : "warning"](
        `${it.label || it.key} · ${r.data.state} · ${r.data.detail}`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyKey(null); }
  };

  const disconnect = async (it) => {
    if (!isOwner) return;
    if (!window.confirm(`Disconnect ${it.label || it.key}? Credentials will be revoked and zeroed.`)) return;
    setBusyKey(it.key);
    try {
      await api.post(`/integrations/${it.key}/disconnect`);
      toast.success(`${it.label || it.key} disconnected.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyKey(null); }
  };

  const c = truth?.counts;
  const subtitle = c
    ? `${truth.providers.length} providers · ${c.connected_verified} connected and verified · ${c.configured_not_authorized} configured but not authorized · ${c.available_to_activate} available to activate · ${c.disconnected} disconnected · ${c.externally_blocked} externally blocked`
    : "Loading provider status…";

  return (
    <AdminAppShell
      activeNav="integrations"
      title="Integrations & API"
      subtitle={subtitle}
      breadcrumbs={[
        { label: "Owner Command Center", to: "/owner" },
        { label: "Integrations & API", to: null },
      ]}
      user={user}
    >
      <div
        className="border border-erf-borderCream bg-erf-cream text-erf-dark rounded-md px-4 py-3 mb-4 text-sm font-nav flex items-start gap-2 mx-auto w-full max-w-[1600px] box-border md:px-6 lg:px-8"
        data-testid="integrations-notice"
      >
        <ShieldCheck className="w-4 h-4 mt-0.5 text-erf-gold shrink-0" aria-hidden="true" />
        <div>
          <strong className="font-display font-semibold">Truthful lifecycle enforced.</strong>{" "}
          Every adapter reports its real state. Credentials are encrypted at rest with Fernet. No adapter is labeled <em>Connected and Verified</em> unless a recorded live check with the provider succeeded.
        </div>
      </div>
      <IntegrationTruthPanel data={truth} error={truthError} />
      <IntegrationErrorHistory />

      {/* R1-fluid — center content inside AdminAppShell's main region and let
          it fluidly grow to 1600px on real desktops. Equal left/right padding
          at every width via responsive px-* scale. Grid breakpoints preserved
          (cols-1 → md:2 → lg:3) so multi-column engages at ≥1024. */}
      <div className="space-y-6 mx-auto w-full max-w-[1600px] box-border px-4 md:px-6 lg:px-8">
        <div>
          <h2 className="font-display font-semibold text-erf-dark" data-testid="tenant-adapters-heading">
            Tenant adapters{truth ? ` (${items.length} of ${truth.providers.length} providers)` : ""}
          </h2>
          <p className="text-xs font-nav text-erf-dark/70 mt-1" data-testid="tenant-adapters-note">
            Adapters with per-tenant credentials. This is a subset of the providers listed above.
          </p>
        </div>
        {Object.keys(grouped).sort().map((tier) => (
          <section key={tier}>
            <div className="text-xs uppercase font-nav text-erf-dark/60 mb-2 tracking-widest">
              {TIER_LABEL[tier] || `Tier ${tier}`}
            </div>
            <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {grouped[tier].map((it) => {
                const isConnected = (it.display_state || it.state) === "connected";
                const isBusy = busyKey === it.key;
                const t = truth?.adapters?.[it.key];
                const checkedAt = t?.last_check_at || it.last_check_at;
                const checkDetail = t?.last_check_at ? t.last_check : it.last_check_detail;
                return (
                  <Card
                    key={it.key}
                    className="ds-card border-erf-borderCream"
                    data-testid={`integration-card-${it.key}`}
                  >
                    <CardContent className="p-4 space-y-3">
                      {/* GLOBAL RESPONSIVE REPAIR — ds-card-header primitive.
                          On <480px (phones) title + chip stack vertically so
                          a long chip label ("Configured but not authorized")
                          can never collapse the heading column to character
                          width. On ≥480px, side-by-side preserved. */}
                      <div className="ds-card-header">
                        <div className="ds-card-header__title">
                          <div className="font-display font-semibold text-erf-dark"
                               data-testid={`integration-card-title-${it.key}`}>
                            {it.label || it.key}
                          </div>
                          <div className="text-[11px] font-mono text-erf-dark/60 mt-0.5 ds-text-safe">
                            {it.key} · {it.category}
                          </div>
                        </div>
                        <div className="ds-card-header__chip">
                          {t
                            ? <StatusChip status={t.state} testId={`integration-status-${it.key}`} />
                            : <span className="text-[11px] font-nav text-erf-dark/60" data-testid={`integration-status-${it.key}`}>Loading…</span>}
                        </div>
                      </div>

                      <div className="text-[11px] font-nav text-erf-dark/70 leading-snug line-clamp-2">
                        {it.description || "No description."}
                      </div>

                      {it.masked_preview && (
                        <div
                          className="text-[10px] font-mono text-erf-dark/60 bg-erf-borderCream/30 rounded px-2 py-1"
                          data-testid={`integration-masked-${it.key}`}
                        >
                          {Object.entries(it.masked_preview).slice(0, 2).map(([k, v]) => (
                            <div key={k}>{k}: {v}</div>
                          ))}
                          {it.use_emergent_llm_key && (
                            <div className="text-erf-gold mt-1">
                              routed via Emergent Universal Key
                            </div>
                          )}
                          {it.key === "sendgrid" && it.masked_preview?.webhook_public_key && (
                            <div
                              className="text-erf-gold mt-1"
                              data-testid={`integration-webhook-configured-${it.key}`}
                            >
                              Signed Event Webhook · Configured
                            </div>
                          )}
                        </div>
                      )}

                      {t && (
                        <div className="text-[11px] font-nav text-erf-dark/80" data-testid={`integration-truth-note-${it.key}`}>
                          {t.level && <span className="font-semibold" data-testid={`integration-level-${it.key}`}>{LEVEL_LABEL[t.level]}. </span>}
                          {t.note}
                        </div>
                      )}
                      <div className="text-[10px] font-mono text-erf-dark/60" data-testid={`integration-last-check-${it.key}`}>
                        {checkedAt ? `Last check ${when(checkedAt)}` : "No check recorded"}
                        {checkedAt && checkDetail && (
                          <div className="text-erf-dark/50 mt-0.5">{checkDetail}</div>
                        )}
                      </div>

                      {/* GLOBAL RESPONSIVE REPAIR — ds-action-bar primitive.
                          On <480px, buttons stack vertically full-width with
                          ≥44px tap targets. On ≥480px, inline and wrap. */}
                      <div className="ds-action-bar border-t border-erf-borderCream">
                        {/* Stage A · Twilio · discoverable navigation.
                            When the twilio card is CONNECTED show "Open SMS Settings";
                            when it is missing fields / not_configured / any other honest
                            non-connected state show "Complete Twilio Setup". Both link
                            to the canonical Communications Settings route + anchor. */}
                        {it.key === "twilio" && (
                          <Link
                            to="/admin/settings/communications?section=twilio#twilio-sms"
                            aria-label={isConnected ? "Open SMS Settings" : "Complete Twilio Setup"}
                          >
                            <Button
                              size="sm"
                              variant="outline"
                              className="min-h-[44px] border-erf-gold text-erf-dark"
                              data-testid={isConnected
                                ? "integration-twilio-open-sms-settings"
                                : "integration-twilio-complete-setup"}
                            >
                              <MessageSquare className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              {isConnected ? "Open SMS Settings" : "Complete Twilio Setup"}
                            </Button>
                          </Link>
                        )}
                        {!isConnected && !(t && NO_CONNECT_STATES.has(t.state)) && (
                          it.key === "microsoft_365" ? (
                            /* STEP 2 Physical-Device Repair — native
                               <a target="_top"> so iPad Safari honors
                               the top-level cross-origin navigation. No
                               onClick handler; `href` + `target=_top`
                               is the entire mechanism. The browser
                               carries the HttpOnly access_token cookie
                               to the backend GET, which 302s to
                               Microsoft's consent screen. */
                            canConnect ? (
                              <a
                                href={M365_CONNECT_START}
                                target="_top"
                                rel="noopener"
                                aria-label="Connect Microsoft 365 (Microsoft consent)"
                                title="Grant Microsoft consent"
                                className="inline-flex items-center justify-center gap-1 rounded-md bg-erf-dark text-erf-cream hover:bg-erf-dark/90 min-h-[44px] px-3 text-sm font-medium no-underline"
                                data-testid="integration-connect-microsoft_365"
                              >
                                <Plug className="w-3.5 h-3.5" aria-hidden="true" />
                                Connect (Microsoft consent)
                              </a>
                            ) : (
                              <Button
                                size="sm"
                                disabled
                                aria-disabled
                                aria-label="Connect Microsoft 365 — Owner only"
                                title="Owner-only action"
                                className="bg-erf-dark text-erf-cream min-h-[44px]"
                                data-testid="integration-connect-disabled-microsoft_365"
                              >
                                <Plug className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                                Connect (Microsoft consent)
                              </Button>
                            )
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => openConnect(it)}
                              disabled={!canConnect || isBusy}
                              aria-disabled={!canConnect}
                              aria-label={canConnect
                                ? `Connect ${it.label || it.key}`
                                : `Connect ${it.label || it.key} — Owner only`}
                              title={canConnect ? "Provide credentials" : "Owner-only action"}
                              className="bg-erf-dark text-erf-cream hover:bg-erf-dark/90 min-h-[44px]"
                              data-testid={canConnect
                                ? `integration-connect-${it.key}`
                                : `integration-connect-disabled-${it.key}`}
                            >
                              <Plug className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              Connect
                            </Button>
                          )
                        )}
                        {/* Secondary: credential-drawer path for M365.
                            Visible only for microsoft_365 and only to Owner.
                            Primary action above routes to OAuth consent;
                            this escape hatch exists for the rare case where
                            Owner needs to view / rotate credentials via the
                            drawer path without first completing OAuth. */}
                        {!isConnected && it.key === "microsoft_365" && canConnect && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openConnect(it)}
                            disabled={isBusy}
                            aria-label="Edit Microsoft 365 credentials"
                            className="min-h-[44px]"
                            data-testid="integration-microsoft_365-edit-credentials"
                          >
                            Edit credentials
                          </Button>
                        )}
                        {isConnected && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verify(it)}
                              disabled={!canVerify || isBusy}
                              aria-label={`Manually verify ${it.label || it.key}`}
                              className="min-h-[44px]"
                              data-testid={canVerify
                                ? `integration-verify-${it.key}`
                                : `integration-verify-disabled-${it.key}`}
                            >
                              <PlayCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRotate(it)}
                              disabled={!isOwner || isBusy}
                              aria-label={`Rotate credentials for ${it.label || it.key}`}
                              className="min-h-[44px]"
                              data-testid={isOwner
                                ? `integration-rotate-${it.key}`
                                : `integration-rotate-disabled-${it.key}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              Rotate
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => disconnect(it)}
                              disabled={!isOwner || isBusy}
                              aria-label={`Disconnect ${it.label || it.key}`}
                              className="text-red-700 border-red-200 hover:bg-red-50 min-h-[44px]"
                              data-testid={isOwner
                                ? `integration-disconnect-${it.key}`
                                : `integration-disconnect-disabled-${it.key}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              Disconnect
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <IntegrationConnectDrawer
        open={drawer.open}
        adapter={drawer.adapter}
        mode={drawer.mode}
        onClose={() => setDrawer({ open: false, adapter: null, mode: "connect" })}
        onSaved={load}
      />
    </AdminAppShell>
  );
}
